import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function normalizeUtc(iso: string): string {
  // D1 CURRENT_TIMESTAMP returns UTC without 'Z' suffix — append it so JS parses as UTC
  if (!iso.endsWith('Z') && !iso.includes('+') && !/\d{2}:\d{2}:\d{2}-\d/.test(iso)) {
    return iso.replace(' ', 'T') + 'Z';
  }
  return iso;
}

export function formatDate(iso: string): string {
  return new Date(normalizeUtc(iso)).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatDateTime(iso: string): string {
  const d = new Date(normalizeUtc(iso));
  const datePart = d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
}

export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(normalizeUtc(dateStr)).getTime();
  const hours = Math.floor(diff / 36e5);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(dateStr);
}

export function formatRelativeHours(hours: number) {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

export function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

export function statusColor(status: string) {
  switch (status.toLowerCase()) {
    case 'open':
      return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300';
    case 'assigned':
      return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
    case 'in progress':
      return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
    case 'waiting for user':
    case 'waiting for team':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'resolved':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'closed':
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    case 'reopened':
      return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    default:
      return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300';
  }
}

export function priorityColor(priority: string) {
  switch (priority.toLowerCase()) {
    case 'critical':
      return 'text-rose-600 bg-rose-50 dark:bg-rose-900/20 dark:text-rose-400';
    case 'high':
      return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
    case 'medium':
      return 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400';
    default:
      return 'text-zinc-600 bg-zinc-50 dark:bg-zinc-800 dark:text-zinc-300';
  }
}

/** Future-tense counterpart of timeAgo: "3h 20m left", "45m left", "overdue". */
export function formatTimeRemaining(iso: string | null | undefined): string {
  if (!iso) return '';
  const target = new Date(normalizeUtc(iso)).getTime();
  const diff = target - Date.now();
  if (diff <= 0) return 'overdue';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '<1m left';
  if (mins < 60) return `${mins}m left`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 48) return m > 0 ? `${h}h ${m}m left` : `${h}h left`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h left`;
}
